/**
 * What kind of file this is, said once (task 828).
 *
 * The kind is the first thing a reader sorts by — a document is opened, a
 * dataset is fed to something else — so it gets an icon and a word rather than
 * being left to the content type, which is precise and unreadable. Both the
 * inline card and the list row use this, so the two can never disagree about
 * what a `DATASET` looks like.
 */
import {
  DocumentTextIcon,
  PaperClipIcon,
  PhotoIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import type { ArtifactKind } from '@/types/artifacts';

const KIND_STYLE: Record<
  ArtifactKind,
  { label: string; icon: typeof DocumentTextIcon; classes: string }
> = {
  DOCUMENT: {
    label: 'Document',
    icon: DocumentTextIcon,
    classes: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  },
  DATASET: {
    label: 'Dataset',
    icon: TableCellsIcon,
    classes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  IMAGE: {
    label: 'Image',
    icon: PhotoIcon,
    classes: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  },
  FILE: {
    label: 'File',
    icon: PaperClipIcon,
    classes: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  },
};

interface ArtifactKindBadgeProps {
  kind: ArtifactKind;
  /** Icon only — for the tight left edge of an inline card. */
  iconOnly?: boolean;
  className?: string;
}

export function ArtifactKindBadge({ kind, iconOnly, className }: ArtifactKindBadgeProps) {
  // An unknown kind is a new enum member the app has not shipped support for
  // yet, not a bug: fall back to the generic one rather than rendering nothing.
  const style = KIND_STYLE[kind] ?? KIND_STYLE.FILE;
  const Icon = style.icon;

  if (iconOnly) {
    return (
      <span
        data-testid="artifact-kind-icon"
        data-kind={kind}
        aria-label={style.label}
        title={style.label}
        className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${style.classes} ${className ?? ''}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
    );
  }

  return (
    <span
      data-testid="artifact-kind-badge"
      data-kind={kind}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${style.classes} ${className ?? ''}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {style.label}
    </span>
  );
}

export default ArtifactKindBadge;
